export type EnsAlertType = 'FRAUD' | 'TASK' | 'POLICY' | 'CUSTOM';

export type EnsAlertStatus = 'PENDING' | 'ESCALATING' | 'CONFIRMED' | 'FAILED' | 'CANCELLED';

export type EnsRecipientStatus = 'PENDING' | 'CONFIRMED' | 'FAILED' | 'CANCELLED' | 'SKIPPED';

export type EnsChannel = 'PUSH' | 'INTERNAL_CALL' | 'SMS' | 'PSTN' | 'WHATSAPP';

export type EnsConfirmationVia = 'PUSH' | 'INTERNAL_CALL' | 'SMS' | 'PSTN' | 'ADMIN_OVERRIDE';

export const ENS_ALERT_TYPES: EnsAlertType[] = ['FRAUD', 'TASK', 'POLICY', 'CUSTOM'];

export const ENS_ALERT_STATUSES: EnsAlertStatus[] = [
    'PENDING',
    'ESCALATING',
    'CONFIRMED',
    'FAILED',
    'CANCELLED',
];

export const ENS_CHANNELS: EnsChannel[] = ['PUSH', 'INTERNAL_CALL', 'SMS', 'PSTN', 'WHATSAPP'];

export const AVAILABLE_ENS_CHANNELS: EnsChannel[] = ['PUSH', 'INTERNAL_CALL', 'SMS', 'PSTN'];

export interface EnsLevelInputDto {
    sequenceNo: number;
    channel: EnsChannel;
    delaySecondsFromPrevious: number;
}

export interface EnsLevelDto {
    id: number;
    sequenceNo: number;
    channel: EnsChannel;
    delaySecondsFromPrevious: number;
    scheduledAt: string;
}

export interface EnsVoicePrompt {
    originalFileName: string;
    bucketFileName: string;
}

export type EnsTtsLanguage =
    | 'ENGLISH'
    | 'POLISH'
    | 'CZECH'
    | 'GERMAN'
    | 'SPANISH'
    | 'CHINESE'
    | 'FRENCH'
    | 'ITALIAN'
    | 'JAPANESE';

export type EnsTtsGender = 'UNSPECIFIED' | 'MALE' | 'FEMALE' | 'NEUTRAL';

export interface EnsTtsRequest {
    text: string;
    language: EnsTtsLanguage;
    gender: EnsTtsGender;
}

export const ENS_TTS_LANGUAGES: EnsTtsLanguage[] = [
    'ENGLISH',
    'POLISH',
    'CZECH',
    'GERMAN',
    'SPANISH',
    'CHINESE',
    'FRENCH',
    'ITALIAN',
    'JAPANESE',
];

export const ENS_TTS_GENDERS: EnsTtsGender[] = ['UNSPECIFIED', 'MALE', 'FEMALE', 'NEUTRAL'];

export const ENS_TTS_MAX_TEXT_LENGTH = 5000;

export interface EnsAlertSummary {
    id: number;
    publicId: string;
    type: EnsAlertType;
    name: string;
    message: string;
    voicePrompt: EnsVoicePrompt | null;
    status: EnsAlertStatus;
    currentLevelSequence: number | null;
    triggeredAt: string;
    triggeredByUserId: number | null;
    confirmedCount: number;
    pendingCount: number;
    failedCount: number;
    targetCount: number;
    targetGroupIds?: number[];
}

export interface EnsRecipient {
    id: number;
    userId: number;
    status: EnsRecipientStatus;
    currentLevelSequence: number | null;
    confirmedAt: string | null;
    confirmedVia: EnsConfirmationVia | null;
    externalMsisdn: number | null;
}

export type EnsNotificationStatus =
    | 'PENDING'
    | 'DISPATCH_REQUESTED'
    | 'DISPATCHED'
    | 'DELIVERED'
    | 'FAILED'
    | 'CONFIRMED'
    | 'CANCELLED'
    | 'TIMEOUT';

export interface EnsNotification {
    id: number;
    recipientId: number;
    recipientUserId: number;
    levelId: number;
    levelSequence: number;
    channel: EnsChannel;
    status: EnsNotificationStatus;
    scheduledAt: string;
    dispatchRequestedAt: string | null;
    dispatchedAt: string | null;
    deliveredAt: string | null;
    failedAt: string | null;
    confirmedAt: string | null;
    updatedAt: string;
    lastError: string | null;
    attemptCount: number;
    mobilePushSent: boolean;
}

export interface EnsAlertDetails {
    summary: EnsAlertSummary;
    targetGroupIds: number[];
    targetUserIds: number[];
    levels: EnsLevelDto[];
    recipients: EnsRecipient[];
    notifications: EnsNotification[];
}

export interface EnsAlertStats {
    periodDays: number;
    alertsToday: number;
    confirmationRate: number;
    pendingCount: number;
    failedCount: number;
    totalAlerts: number;
    activeAlerts: number;
}

export interface EnsAlertPreset {
    type: EnsAlertType;
    levels: EnsLevelInputDto[];
}

export interface EnsAlertTemplate {
    id: number | null;
    systemKey: EnsAlertType | null;
    system: boolean;
    type: EnsAlertType;
    name: string;
    message: string;
    targetGroupIds: number[];
    targetUserIds: number[];
    targetUsers: EnsUserContactListItem[];
    levels: EnsLevelInputDto[];
    voicePrompt: EnsVoicePrompt | null;
    apiTriggerEnabled: boolean;
    sourceAlertPublicId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    createdByUserId: number | null;
    updatedByUserId: number | null;
}

export interface EnsConfiguration {
    maxActiveAlertsPerUser: number;
    alertHistoryRetentionDays: number;
    finalLevelTimeoutSeconds: number;
    smsConfirmationKeywords: string[];
    dtmfConfirmationKey: string;
    adminOverrideAuditEnabled: boolean;
    callerMsisdnId: number | null;
    callerMsisdn?: number | null;
    smsSenderMsisdnId?: number | null;
    smsSenderMsisdn?: number | null;
}

export type UpdateEnsConfigurationRequest = Pick<
    EnsConfiguration,
    | 'maxActiveAlertsPerUser'
    | 'alertHistoryRetentionDays'
    | 'finalLevelTimeoutSeconds'
    | 'smsConfirmationKeywords'
    | 'dtmfConfirmationKey'
    | 'adminOverrideAuditEnabled'
>;

export interface EnsUserContact {
    userId: number;
    externalMsisdn: number | null;
    ensEligible: boolean;
    updatedAt: string;
    updatedByUserId: number | null;
}

export interface EnsUserContactListItem {
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl: string | null;
    position: string | null;
    externalMsisdn: number | null;
    ensEligible: boolean;
    updatedAt: string | null;
    updatedByUserId: number | null;
}

export interface EnsMyPendingAlert {
    publicId: string;
    type: EnsAlertType;
    name: string;
    message: string;
    triggeredAt: string;
    currentLevelSequence: number | null;
    notificationChannel: EnsChannel | null;
}

export interface EnsMyAlert extends EnsMyPendingAlert {
    recipientStatus: EnsRecipientStatus;
    alertStatus: EnsAlertStatus;
    confirmedAt: string | null;
}

export interface EnsAlertNotification {
    notificationId: number;
    publicId: string;
    name: string;
    message: string;
    occurredAt: string;
    nextLevelScheduledAt: string | null;
}

export interface EnsAlertNotificationsResponse {
    data: EnsAlertNotification[];
    nextAfterNotificationId: number;
    hasMore: boolean;
}

export interface EnsAlertListFilter {
    phrase?: string;
    statuses?: EnsAlertStatus[];
    types?: EnsAlertType[];
    triggeredFrom?: string;
    triggeredTo?: string;
}

export interface PaginationArgs {
    offset?: number;
    limit?: number;
}

export interface PageResponse<T> {
    data: T[];
    totalCount: number;
}

export interface CreateEnsAlertRequest {
    type: EnsAlertType;
    name: string;
    message: string;
    targetGroupIds: number[];
    targetUserIds?: number[];
    levels?: EnsLevelInputDto[];
    voicePrompt?: EnsVoicePrompt | null;
}

export interface SaveEnsAlertTemplateRequest {
    type: EnsAlertType;
    name: string;
    message?: string;
    targetGroupIds: number[];
    targetUserIds?: number[];
    levels?: EnsLevelInputDto[];
    voicePrompt?: EnsVoicePrompt | null;
    apiTriggerEnabled: boolean;
}

export type EnsUploadAudioResponse = Partial<EnsVoicePrompt>;
